import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  BookOpen,
  Users,
  UserCircle,
  UserCheck,
  Building2,
  Tags,
  Bell,
  MessageSquare,
  BarChart3,
  ScrollText,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Menu,
  X,
  Handshake,
  FileText,
  LayoutGrid,
  Landmark,
  Globe,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ThemeToggle } from "./ThemeToggle";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { getOverviewStats, type OverviewStats } from "@/api/stats";

interface SidebarItem {
  key: string;
  icon: React.ElementType;
  path: string;
  label: string;
  /** Optional badge count shown next to the label. */
  badge?: number;
}

export function AdminLayout() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const lang = i18n.language || "fr";

  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [badgeCounts, setBadgeCounts] = useState<OverviewStats | null>(null);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Fetch badge counts (pending theses, pending claims, etc.)
  const fetchBadgeCounts = useCallback(async () => {
    try {
      const stats = await getOverviewStats();
      setBadgeCounts(stats);
    } catch {
      // Silently fail — badges are non-critical
    }
  }, []);

  useEffect(() => {
    fetchBadgeCounts();
    // Refresh badge counts every 2 minutes
    const interval = setInterval(fetchBadgeCounts, 120_000);
    return () => clearInterval(interval);
  }, [fetchBadgeCounts]);

  const sidebarItems: SidebarItem[] = [
    {
      key: "dashboard",
      icon: LayoutDashboard,
      path: `/${lang}/admin`,
      label: t("admin.dashboard"),
    },
    {
      key: "theses",
      icon: BookOpen,
      path: `/${lang}/admin/theses`,
      label: t("admin.manage_theses"),
      badge: badgeCounts?.pending_theses || undefined,
    },
    {
      key: "documents",
      icon: FileText,
      path: `/${lang}/admin/documents`,
      label: t("admin.manage_documents"),
      badge: badgeCounts?.pending_documents || undefined,
    },
    {
      key: "authors",
      icon: UserCircle,
      path: `/${lang}/admin/authors`,
      label: t("admin.manage_authors"),
    },
    {
      key: "claims",
      icon: UserCheck,
      path: `/${lang}/admin/authors/claims`,
      label: t("admin.author_claims"),
      badge: badgeCounts?.pending_claims || undefined,
    },
    {
      key: "users",
      icon: Users,
      path: `/${lang}/admin/users`,
      label: t("admin.manage_users"),
    },
    {
      key: "partners",
      icon: Handshake,
      path: `/${lang}/admin/partners`,
      label: t("admin.manage_partners"),
    },
    {
      key: "departments",
      icon: Building2,
      path: `/${lang}/admin/departments`,
      label: t("admin.manage_departments"),
    },
    {
      key: "categories",
      icon: Tags,
      path: `/${lang}/admin/categories`,
      label: t("admin.manage_categories"),
    },
    {
      key: "sections",
      icon: LayoutGrid,
      path: `/${lang}/admin/sections`,
      label: t("admin.manage_sections"),
    },
    {
      key: "institutions",
      icon: Landmark,
      path: `/${lang}/admin/institutions`,
      label: t("admin.manage_institutions"),
    },
    {
      key: "countries",
      icon: Globe,
      path: `/${lang}/admin/countries`,
      label: t("admin.manage_countries"),
    },
    {
      key: "notifications",
      icon: Bell,
      path: `/${lang}/admin/notifications`,
      label: t("admin.notifications"),
    },
    {
      key: "reviews",
      icon: MessageSquare,
      path: `/${lang}/admin/reviews`,
      label: t("admin.reviews"),
      badge: badgeCounts?.total_reviews ? undefined : undefined,
    },
    {
      key: "reports",
      icon: BarChart3,
      path: `/${lang}/admin/reports`,
      label: t("admin.reports"),
    },
    {
      key: "audit",
      icon: ScrollText,
      path: `/${lang}/admin/audit-log`,
      label: t("admin.audit_log"),
    },
    {
      key: "settings",
      icon: Settings,
      path: `/${lang}/admin/settings`,
      label: t("admin.settings"),
    },
  ];

  function isActive(path: string): boolean {
    // Exact match for dashboard, prefix match for others
    if (path === `/${lang}/admin`) {
      return location.pathname === path || location.pathname === `${path}/`;
    }
    return location.pathname.startsWith(path);
  }

  // Build breadcrumbs from the current path
  function getBreadcrumbs(): Array<{ label: string; path?: string }> {
    const crumbs: Array<{ label: string; path?: string }> = [
      { label: t("admin.dashboard"), path: `/${lang}/admin` },
    ];

    const segments = location.pathname
      .replace(`/${lang}/admin`, "")
      .split("/")
      .filter(Boolean);

    if (segments.length > 0) {
      const matchedItem = sidebarItems.find(
        (item) =>
          location.pathname.startsWith(item.path) &&
          item.path !== `/${lang}/admin`,
      );
      if (matchedItem) {
        crumbs.push({ label: matchedItem.label });
      }
    }

    return crumbs;
  }

  const breadcrumbs = getBreadcrumbs();

  async function handleLogout() {
    await logout();
    navigate(`/${lang}/login`);
  }

  // ── Sidebar Content (shared between desktop and mobile) ────────────────

  const SidebarContent = ({ isMobile = false }: { isMobile?: boolean }) => (
    <div className="flex flex-col h-full">
      {/* Logo / Brand */}
      <div
        className={cn(
          "flex items-center border-b border-sidebar-border px-4 h-16 shrink-0",
          collapsed && !isMobile ? "justify-center" : "justify-between",
        )}
      >
        {(!collapsed || isMobile) && (
          <Link
            to={`/${lang}/admin`}
            className="flex items-center gap-2 font-heading text-lg font-bold text-sidebar-foreground hover:text-sidebar-primary transition-colors"
          >
            <BookOpen className="h-6 w-6 text-sidebar-primary" />
            <span>Katalog</span>
          </Link>
        )}

        {/* Collapse toggle (desktop only) */}
        {!isMobile && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 rounded-md text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors"
            aria-label={collapsed ? t("sidebar.expand") : t("sidebar.collapse")}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        )}

        {/* Close button (mobile only) */}
        {isMobile && (
          <button
            onClick={() => setMobileOpen(false)}
            className="p-1.5 rounded-md text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors"
            aria-label={t("accessibility.close_menu")}
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
        {sidebarItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);

          return (
            <Link
              key={item.key}
              to={item.path}
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 group relative",
                active
                  ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm"
                  : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground",
                collapsed && !isMobile ? "justify-center px-2" : "",
              )}
              title={collapsed && !isMobile ? item.label : undefined}
            >
              <Icon
                className={cn(
                  "shrink-0 transition-colors",
                  active ? "h-5 w-5" : "h-5 w-5",
                )}
              />
              {(!collapsed || isMobile) && (
                <span className="flex-1">{item.label}</span>
              )}

              {/* Badge count */}
              {item.badge !== undefined && item.badge > 0 && (
                <span
                  className={cn(
                    "inline-flex items-center justify-center rounded-full text-[10px] font-bold leading-none",
                    collapsed && !isMobile
                      ? "absolute -top-1 -right-1 h-4 min-w-[16px] px-1 bg-destructive text-destructive-foreground"
                      : "ml-auto h-5 min-w-[20px] px-1.5 bg-destructive/15 text-destructive",
                  )}
                >
                  {item.badge > 99 ? "99+" : item.badge}
                </span>
              )}

              {/* Active indicator bar */}
              {active && (
                <motion.div
                  layoutId="admin-sidebar-indicator"
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full bg-sidebar-primary-foreground"
                  transition={{
                    type: "spring",
                    stiffness: 350,
                    damping: 30,
                  }}
                />
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom section: user info + logout */}
      <div className="border-t border-sidebar-border p-3 space-y-2 shrink-0">
        {/* Theme & Language toggles */}
        {(!collapsed || isMobile) && (
          <div className="flex items-center justify-between px-2 py-1">
            <ThemeToggle />
            <LanguageSwitcher />
          </div>
        )}

        {/* User info */}
        {user && (
          <div
            className={cn(
              "flex items-center gap-3 rounded-lg px-3 py-2",
              collapsed && !isMobile ? "justify-center px-2" : "",
            )}
          >
            <div className="w-8 h-8 rounded-full bg-sidebar-primary/20 flex items-center justify-center text-sidebar-primary font-medium text-sm shrink-0">
              {user.first_name.charAt(0)}
              {user.last_name.charAt(0)}
            </div>
            {(!collapsed || isMobile) && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-sidebar-foreground truncate">
                  {user.first_name} {user.last_name}
                </p>
                <p className="text-xs text-sidebar-foreground/60 truncate">
                  {user.role}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Logout */}
        <button
          onClick={handleLogout}
          className={cn(
            "flex items-center gap-3 w-full rounded-lg px-3 py-2 text-sm font-medium text-sidebar-foreground/70 hover:bg-destructive/10 hover:text-destructive transition-colors",
            collapsed && !isMobile ? "justify-center px-2" : "",
          )}
          title={collapsed && !isMobile ? t("nav.logout") : undefined}
        >
          <LogOut className="h-5 w-5 shrink-0" />
          {(!collapsed || isMobile) && <span>{t("nav.logout")}</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* ── Desktop Sidebar ──────────────────────────────────────────────── */}
      <aside
        className={cn(
          "hidden lg:flex flex-col bg-sidebar border-r border-sidebar-border transition-all duration-300 ease-in-out shrink-0",
          collapsed ? "w-[68px]" : "w-64",
        )}
      >
        <SidebarContent />
      </aside>

      {/* ── Mobile Sidebar Overlay ───────────────────────────────────────── */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-black/50 lg:hidden"
              onClick={() => setMobileOpen(false)}
            />

            {/* Sidebar Panel */}
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed inset-y-0 left-0 z-50 w-72 bg-sidebar border-r border-sidebar-border lg:hidden"
            >
              <SidebarContent isMobile />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ── Main Content Area ─────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Header */}
        <header className="flex items-center justify-between h-16 border-b border-border bg-background px-4 lg:px-6 shrink-0">
          {/* Left: mobile hamburger + breadcrumbs */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="p-2 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors lg:hidden"
              aria-label={t("accessibility.open_menu")}
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* Breadcrumbs */}
            <nav className="hidden sm:flex items-center gap-1.5 text-sm">
              {breadcrumbs.map((crumb, index) => (
                <span key={index} className="flex items-center gap-1.5">
                  {index > 0 && (
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
                  )}
                  {crumb.path && index < breadcrumbs.length - 1 ? (
                    <Link
                      to={crumb.path}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-foreground font-medium">
                      {crumb.label}
                    </span>
                  )}
                </span>
              ))}
            </nav>
          </div>

          {/* Right: quick actions */}
          <div className="flex items-center gap-2">
            {/* Back to site link */}
            <Link
              to={`/${lang}/`}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden sm:inline-flex items-center gap-1"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              {t("nav.home")}
            </Link>

            {/* Desktop theme & language (shown when sidebar is collapsed) */}
            {collapsed && (
              <div className="hidden lg:flex items-center gap-2">
                <ThemeToggle />
                <LanguageSwitcher />
              </div>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="container py-6 px-4 lg:px-8 max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

// ─── Placeholder sub-components referenced from layout ──────────────────────
// These are simple wrappers that can be replaced once the real components exist.

export default AdminLayout;
